import type { LoginChallenge, LoginEnvelope } from "@/api/auth";

const encoder = new TextEncoder();

export async function encryptLoginPayload(
  challenge: LoginChallenge,
  credentials: { username: string; password: string; captcha: string },
): Promise<LoginEnvelope> {
  if (
    challenge.crypto.version !== 1 ||
    challenge.crypto.alg !== "RSA-OAEP-256" ||
    challenge.crypto.enc !== "A256GCM"
  ) {
    throw new Error("服务端登录加密协议不受支持");
  }
  if (Date.parse(challenge.expiresAt) <= Date.now()) {
    throw new Error("登录验证码已过期，请刷新后重试");
  }

  const publicKey = await window.crypto.subtle.importKey(
    "jwk",
    challenge.crypto.publicKeyJwk,
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"],
  );
  const aesKey = await window.crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt"],
  );
  const rawAesKey = await window.crypto.subtle.exportKey("raw", aesKey);
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const aad = encoder.encode(
    `login-v1|${challenge.crypto.kid}|${challenge.challengeId}`,
  );
  const plaintext = encoder.encode(JSON.stringify(credentials));
  const [wrappedKey, ciphertext] = await Promise.all([
    window.crypto.subtle.encrypt({ name: "RSA-OAEP" }, publicKey, rawAesKey),
    window.crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv,
        additionalData: aad,
        tagLength: 128,
      },
      aesKey,
      plaintext,
    ),
  ]);

  return {
    version: 1,
    kid: challenge.crypto.kid,
    challengeId: challenge.challengeId,
    wrappedKey: toBase64Url(wrappedKey),
    iv: toBase64Url(iv),
    ciphertext: toBase64Url(ciphertext),
  };
}

function toBase64Url(value: ArrayBuffer | Uint8Array) {
  const bytes = value instanceof Uint8Array ? value : new Uint8Array(value);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return window
    .btoa(binary)
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replace(/=+$/, "");
}
